import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery } from "@apollo/client";
import { Badge, Dropdown } from "antd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBell } from "@fortawesome/free-solid-svg-icons";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { getCustomerIncidentAlertsQuery, markCustomerIncidentsSeenMutation } from "@src/util/query/incident.query";
import { IncidentStatusTag } from "@src/page/incidentPage/incidentMeta";

dayjs.extend(relativeTime);

// Navbar bell for new incidents on the customer's sites. There's no socket in this
// app, so it polls, and also refetches straight away when public/sw.js relays a
// push (a new incident sends one) or when the tab comes back into focus.
export const IncidentBell = () => {
    const navigate = useNavigate();
    const [open, setOpen] = useState(false);
    // what the list looked like when opened - marking seen refetches with every
    // isNew false, and the "new" dots shouldn't vanish while the panel is open
    const [snapshot, setSnapshot] = useState(null);

    const { data, refetch } = useQuery(getCustomerIncidentAlertsQuery, {
        pollInterval: 60000,
        fetchPolicy: "cache-and-network",
        nextFetchPolicy: "cache-first",
    });
    const [markSeen] = useMutation(markCustomerIncidentsSeenMutation, {
        refetchQueries: [{ query: getCustomerIncidentAlertsQuery }],
    });

    useEffect(() => {
        const onPush = (e) => { if (e.data?.type === "wfc-push") refetch().catch(() => {}); };
        const onVisible = () => { if (document.visibilityState === "visible") refetch().catch(() => {}); };
        navigator.serviceWorker?.addEventListener("message", onPush);
        document.addEventListener("visibilitychange", onVisible);
        return () => {
            navigator.serviceWorker?.removeEventListener("message", onPush);
            document.removeEventListener("visibilitychange", onVisible);
        };
    }, [refetch]);

    const alerts = data?.getCustomerIncidentAlerts?.response;
    const unread = alerts?.unreadCount || 0;
    const recent = (open && snapshot) || alerts?.recent || [];

    const handleOpenChange = (next) => {
        setOpen(next);
        if (next) {
            setSnapshot(alerts?.recent || []);
            if (unread > 0) markSeen().catch(() => {});
        } else {
            setSnapshot(null);
        }
    };

    const go = (path) => { setOpen(false); setSnapshot(null); navigate(path); };

    const panel = (
        <div className="nav-bell-panel">
            <div className="nav-bell-header">
                <span className="nav-bell-title">Incident alerts</span>
                {snapshot?.some((r) => r.isNew) && (
                    <span className="nav-bell-new-count">{snapshot.filter((r) => r.isNew).length} new</span>
                )}
            </div>

            {recent.length === 0
                ? (
                    <div className="nav-bell-empty">No incidents reported on your sites</div>
                )
                : (
                    <div className="nav-bell-list">
                        {recent.map((r) => (
                            <div key={r._id} className={`nav-bell-item${r.isNew ? " nav-bell-item-new" : ""}`} onClick={() => go(`/customer/incidents/${r._id}`)}>
                                <span className="nav-bell-unread-dot" />
                                <div className="nav-bell-item-body">
                                    <div className="nav-bell-item-top">
                                        <span className="nav-bell-item-title">{r.incidentType || "Incident"} reported</span>
                                        <span className="nav-bell-item-time">{dayjs(r.reportedAt).fromNow()}</span>
                                    </div>
                                    <div className="nav-bell-item-site">{r.site || "Your site"}</div>
                                    <IncidentStatusTag status={r.status} />
                                </div>
                            </div>
                        ))}
                    </div>
                )}

            <button type="button" className="nav-bell-footer" onClick={() => go("/customer/incidents")}>
                View all incidents
            </button>
        </div>
    );

    return (
        <Dropdown
            menu={{ items: [] }}
            dropdownRender={() => panel}
            open={open}
            onOpenChange={handleOpenChange}
            trigger={["click"]}
            placement="bottomRight"
        >
            <span className="nav-bell-trigger" title="Incident alerts">
                <Badge count={unread} size="small" overflowCount={99} offset={[2, -2]}>
                    <FontAwesomeIcon icon={faBell} className="nav-bell-icon" />
                </Badge>
            </span>
        </Dropdown>
    );
};
